import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, CheckCircle2, ArrowRight, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Infopaket-Dialog: Name + E-Mail → Flyer + Kursdetails per E-Mail.
 * Bewusst minimal: 2 Felder, 1 Button. exitIntent=true ändert nur die Headline.
 */
export default function InfopaketDialog({ course, open, onOpenChange, exitIntent = false }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setDone(false); setError(null); }
  }, [open]);

  if (!course) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/leads/infopaket`, { name: form.name, email: form.email, course });
      setDone(true);
    } catch {
      setError("Das hat nicht geklappt – bitte versuch es nochmal oder ruf an: 0211 29273330.");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="infopaket-dialog" className="max-w-md bg-cream-50 border-cream-300 rounded-3xl p-7 md:p-8">
        {done ? (
          <div className="text-center py-4" data-testid="infopaket-success">
            <CheckCircle2 className="h-12 w-12 text-sage-dark mx-auto" strokeWidth={1.5} />
            <DialogTitle className="font-display text-2xl text-sand-800 mt-4">
              Unterwegs! 📬
            </DialogTitle>
            <p className="mt-2 text-[14.5px] text-sand-600 leading-relaxed">
              Dein Infopaket zu <strong className="text-sand-800">{course.title}</strong> ist
              auf dem Weg in dein Postfach (bitte auch den Spam-Ordner prüfen).
            </p>
            <a
              href={`https://wa.me/491622572545?text=${encodeURIComponent(`Hallo! Ich interessiere mich für "${course.title}" und habe eine Frage.`)}`}
              target="_blank" rel="noreferrer"
              data-testid="infopaket-whatsapp-link"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-sand-800/25 px-5 py-2.5 text-[13.5px] font-semibold text-sand-800 hover:border-terra hover:text-terra transition-colors"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
              Direkte Frage? Schreib uns bei WhatsApp
            </a>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-terra/12 text-terra">
                <Mail className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <DialogTitle className="font-display text-[1.45rem] leading-tight text-sand-800">
                {exitIntent ? "Bevor du gehst …" : "Dein Infopaket"}
              </DialogTitle>
            </div>
            <p className="text-[14px] text-sand-600 leading-relaxed -mt-1">
              {exitIntent
                ? <>Soll ich dir das Infopaket zu <strong className="text-sand-800">{course.title}</strong> mailen? Flyer, Preise & Förder-Infos – in 10 Sekunden in deinem Postfach.</>
                : <>Flyer, alle Kursdetails & Förder-Infos zu <strong className="text-sand-800">{course.title}</strong> – direkt in dein Postfach.</>}
            </p>
            <form onSubmit={submit} className="mt-2 space-y-3">
              <Input
                required minLength={2} placeholder="Dein Vorname"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="infopaket-name-input"
                className="h-12 rounded-xl bg-white border-cream-300 text-[15px]"
              />
              <Input
                required type="email" placeholder="Deine E-Mail-Adresse"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="infopaket-email-input"
                className="h-12 rounded-xl bg-white border-cream-300 text-[15px]"
              />
              {error && <p data-testid="infopaket-error" className="text-[13px] text-red-600">{error}</p>}
              <Button
                type="submit" disabled={busy}
                data-testid="infopaket-submit-btn"
                className="w-full h-12 rounded-full bg-terra hover:bg-terra-dark text-white text-[15px] font-semibold"
              >
                {busy ? "Wird gesendet …" : "Infopaket zuschicken"}
                {!busy && <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={2} />}
              </Button>
              <p className="text-[11.5px] text-sand-500 leading-snug text-center">
                Kostenlos & unverbindlich. Kein Spam – nur dein Infopaket und eine kurze
                Nachfrage. <a href="/datenschutz" className="underline">Datenschutz</a>
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
